/** Multi-select action bar for the department inboxes (Dean's audit, Oct 2026) — move the whole Cashing Inn thread to Done in one go. */
import { useState } from "react";
import { CheckCircle2, ChevronDown, FolderInput, Loader2, MailOpen, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

export function BulkBar({ count, api, me, ids, folders, currentFolder, onDone, onClear }) {
  const [busy, setBusy] = useState(null);
  const [people, setPeople] = useState(null);
  const loadPeople = () => { if (!people) api.assignees().then((r) => setPeople(r.people || [])).catch(() => setPeople([])); };

  const run = async (label, payload) => {
    setBusy(label);
    try {
      const r = await api.bulk({ message_ids: ids, ...payload });
      const bits = [`${r.done} of ${ids.length} ${label}`];
      if (r.logged) bits.push(`${r.logged} logged on customer files`);
      if (r.unlogged) bits.push(`${r.unlogged} had no file (moved anyway)`);
      if (r.failed?.length) bits.push(`${r.failed.length} failed`);
      (r.failed?.length ? toast.warning : toast.success)(bits.join(" · "));
      onDone?.(r);
    } catch (e) { toast.error(errorDetailMessage(e, e.message)); } finally { setBusy(null); }
  };

  const targets = (folders || []).filter((f) => f.id !== currentFolder && !(currentFolder === "inbox" && f.kind === "inbox") && !(currentFolder === "done" && f.kind === "done"));
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b bg-amber-50 shrink-0 flex-wrap" data-testid="bulk-bar">
      <span className="text-[11px] font-semibold text-amber-900 tabular-nums" data-testid="bulk-count">{count} selected</span>
      <Button size="sm" variant="outline" className="h-6 text-[10px] bg-white" disabled={!!busy} onClick={() => run("moved to Done", { action: "move_done" })} data-testid="bulk-move-done"
        title="Logs each email on its matched customer file first (when it has one), then moves them all to Done">
        {busy === "moved to Done" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}Move to done
      </Button>
      {targets.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className="h-6 text-[10px] bg-white" disabled={!!busy} data-testid="bulk-move-menu-btn"><FolderInput className="w-3 h-3 mr-1" />Move to…<ChevronDown className="w-3 h-3 ml-1 opacity-60" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[320px] overflow-auto min-w-[200px]" data-testid="bulk-move-menu">
            {targets.map((f) => (
              <DropdownMenuItem key={f.id} className="text-xs" onClick={() => run(`moved to ${f.name}`, { action: "move", folder_id: f.kind === "inbox" ? "inbox" : f.kind === "done" ? "done" : f.id })} data-testid={`bulk-move-${f.kind === "folder" ? f.id : f.kind}`}>
                {f.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <DropdownMenu onOpenChange={(o) => o && loadPeople()}>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-6 text-[10px] bg-white" disabled={!!busy} data-testid="bulk-assign-menu-btn"><UserPlus className="w-3 h-3 mr-1" />Assign<ChevronDown className="w-3 h-3 ml-1 opacity-60" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[320px] overflow-auto min-w-[220px]" data-testid="bulk-assign-menu">
          <DropdownMenuItem className="text-xs font-medium" onClick={() => run("assigned to you", { action: "assign", assignee: me?.name || me?.email || "Me", assignee_email: me?.email || "" })} data-testid="bulk-assign-me">Assign to me</DropdownMenuItem>
          <DropdownMenuSeparator />
          {people === null && <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>}
          {[true, false].map((team) => {
            const rows = (people || []).filter((p) => p.name !== (me?.name || "") && !!p.team === team);
            if (!rows.length) return null;
            return (
              <div key={String(team)}>
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">{team ? (api.dept === "legal" ? "Legal team" : "Collections team") : "Everyone else"}</DropdownMenuLabel>
                {rows.map((p) => (
                  <DropdownMenuItem key={p.email || p.name} className="text-xs" onClick={() => run(`assigned to ${p.name}`, { action: "assign", assignee: p.name, assignee_email: p.email })} data-testid={`bulk-assign-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>{p.name}</DropdownMenuItem>
                ))}
              </div>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-xs text-rose-700" onClick={() => run("unassigned", { action: "assign", assignee: "" })} data-testid="bulk-unassign">Unassign</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button size="sm" variant="outline" className="h-6 text-[10px] bg-white" disabled={!!busy} onClick={() => run("marked read", { action: "mark_read" })} data-testid="bulk-mark-read"><MailOpen className="w-3 h-3 mr-1" />Mark read</Button>
      <Button size="sm" variant="ghost" className="h-6 text-[10px] ml-auto" onClick={onClear} data-testid="bulk-clear"><X className="w-3 h-3 mr-1" />Clear</Button>
    </div>
  );
}
